import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ShieldCheck, ShieldAlert, Lock, Activity, RefreshCw,
  Eye, Key, FileText, UserCheck, AlertTriangle, History, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import AuditLogsPage from './AuditLogsPage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_ICONS = {
  login_failed: ShieldAlert,
  password_reset: Key,
  account_unlock: Lock,
  gdpr_pdf_generated: FileText,
  gdpr_export: FileText,
  gdpr_deletion_request: AlertTriangle,
  view_pii: Eye,
  unauthorized_access: Ban,
  account_auto_locked: Lock,
};

const EVENT_LABELS = {
  login_failed: 'Login falliti',
  password_reset: 'Reset password',
  account_unlock: 'Sblocco account',
  gdpr_pdf_generated: 'PDF GDPR generato',
  gdpr_export: 'Export dati GDPR',
  gdpr_deletion_request: 'Richiesta cancellazione',
  view_pii: 'Accesso a dati PII',
  unauthorized_access: 'Accessi negati',
  account_auto_locked: 'Blocco automatico',
};

const ESITO_BADGES = {
  evasa: 'bg-emerald-100 text-emerald-800',
  in_lavorazione: 'bg-amber-100 text-amber-800',
  rifiutata: 'bg-red-100 text-red-800',
};

const SecurityCenterPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/security-center`);
      setData(data);
    } catch {
      toast.error('Errore caricamento centro sicurezza');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('it-IT', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  if (loading || !data) {
    return <div className="p-6 text-slate-500">Caricamento...</div>;
  }

  const tf = data.two_factor;
  const locks = data.locked_accounts;
  const ev = data.recent_events_7d;
  const adoptionColor = tf.adoption_pct >= 75 ? 'text-emerald-700' : tf.adoption_pct >= 30 ? 'text-amber-700' : 'text-red-700';
  const adoptionBar = tf.adoption_pct >= 75 ? 'bg-emerald-500' : tf.adoption_pct >= 30 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="p-6 space-y-6" data-testid="security-center-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-600" />
            Centro Sicurezza
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Cruscotto di sicurezza, compliance e tracciamento attività — monitoraggio in tempo reale dello stato del sistema.
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
        </Button>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList data-testid="security-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <ShieldCheck className="w-4 h-4 mr-2" /> Panoramica
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <History className="w-4 h-4 mr-2" /> Registro Attività
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* 2FA Adoption */}
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => navigate('/admin/utenti')} data-testid="kpi-2fa">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-600">Adozione 2FA utenti</span>
              {tf.adoption_pct >= 50 ? <ShieldCheck className="w-5 h-5 text-emerald-600" /> : <ShieldAlert className="w-5 h-5 text-amber-600" />}
            </div>
            <p className={`text-3xl font-bold ${adoptionColor}`}>{tf.adoption_pct}%</p>
            <p className="text-xs text-slate-500 mt-1">{tf.users_with_2fa} / {tf.users_total} utenti</p>
            <div className="w-full bg-slate-200 rounded-full h-1.5 mt-3">
              <div className={`h-1.5 rounded-full ${adoptionBar}`} style={{ width: `${Math.min(tf.adoption_pct, 100)}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Admin 2FA */}
        <Card data-testid="kpi-admins-2fa">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-600">Admin con 2FA</span>
              <UserCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-3xl font-bold text-emerald-700">{tf.admins_with_2fa} / {tf.admins_total}</p>
            <p className="text-xs text-slate-500 mt-1">
              {tf.admins_with_2fa === tf.admins_total ? 'tutti protetti ✓' : 'configurazione in corso'}
            </p>
          </CardContent>
        </Card>

        {/* Account bloccati */}
        <Card className={locks.current_count > 0 ? 'border-red-300 bg-red-50/30 cursor-pointer hover:bg-red-50/60' : ''}
              onClick={() => locks.current_count > 0 && navigate('/admin/utenti')}
              data-testid="kpi-locked">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-600">Account bloccati ora</span>
              <Lock className={`w-5 h-5 ${locks.current_count > 0 ? 'text-red-600' : 'text-slate-400'}`} />
            </div>
            <p className={`text-3xl font-bold ${locks.current_count > 0 ? 'text-red-700' : 'text-slate-700'}`}>
              {locks.current_count}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {locks.unlocks_last_7d > 0 ? `${locks.unlocks_last_7d} sbloccati negli ultimi 7gg` : 'nessun evento recente'}
            </p>
          </CardContent>
        </Card>

        {/* Eventi sicurezza 7gg */}
        <Card data-testid="kpi-events">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-600">Eventi sicurezza 7gg</span>
              <Activity className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-slate-900">
              {Object.values(ev).reduce((a, b) => a + b, 0)}
            </p>
            <p className="text-xs text-slate-500 mt-1">azioni critiche tracciate</p>
          </CardContent>
        </Card>
      </div>

      {/* Account bloccati - lista */}
      {locks.list && locks.list.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-red-700 flex items-center gap-2">
              <Lock className="w-5 h-5" /> Account bloccati attualmente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Tentativi falliti</TableHead>
                  <TableHead>Bloccato da</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locks.list.map((u) => (
                  <TableRow key={u.id} data-testid={`locked-row-${u.id}`}>
                    <TableCell className="font-medium">{u.cognome} {u.nome}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-xs capitalize">{u.ruolo}</TableCell>
                    <TableCell className="text-sm">{u.failed_login_attempts || 0}</TableCell>
                    <TableCell className="text-xs text-slate-500">{u.locked_at ? fmtDate(u.locked_at) : '—'}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => navigate('/admin/utenti')}>
                        Gestisci →
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Eventi 7gg suddivisi */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" /> Eventi sicurezza ultimi 7 giorni
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(ev).map(([action, count]) => {
              const Icon = EVENT_ICONS[action] || Activity;
              return (
                <div key={action} className="bg-slate-50 rounded p-3 text-center" data-testid={`event-${action}`}>
                  <Icon className="w-5 h-5 mx-auto text-slate-600 mb-1" />
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-600 mt-1">{EVENT_LABELS[action] || action}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tentativi login falliti per IP (ultimi 7gg) */}
      {data.failed_logins_by_ip_7d && data.failed_logins_by_ip_7d.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-amber-700 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Tentativi di login falliti per IP — ultimi 7 giorni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table data-testid="failed-by-ip-table">
              <TableHeader>
                <TableRow>
                  <TableHead>IP origine</TableHead>
                  <TableHead>Tentativi</TableHead>
                  <TableHead>Ultimo tentativo</TableHead>
                  <TableHead>Motivi</TableHead>
                  <TableHead>Email provate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.failed_logins_by_ip_7d.map((f, i) => (
                  <TableRow key={`${f.ip || 'unknown'}-${i}`} className={f.count >= 5 ? 'bg-red-50' : ''}>
                    <TableCell className="font-mono text-xs">{f.ip || 'unknown'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        f.count >= 10 ? 'bg-red-200 text-red-900'
                          : f.count >= 5 ? 'bg-amber-200 text-amber-900'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {f.count}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDate(f.last_attempt)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{(f.reasons || []).join(', ')}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      <div className="max-w-md break-all">{(f.emails_tried || []).join(', ')}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-500 mt-2">
              IP con ≥5 tentativi: <span className="text-amber-700 font-medium">attenzione</span>;
              IP con ≥10 tentativi: <span className="text-red-700 font-medium">possibile brute force</span> —
              valuta blocco a livello firewall/Cloudflare.
            </p>
          </CardContent>
        </Card>
      )}

      {data.unauthorized_access_by_user_7d && data.unauthorized_access_by_user_7d.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-red-700 flex items-center gap-2">
              <Ban className="w-5 h-5" /> Accessi negati a risorse altrui — ultimi 7 giorni
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table data-testid="unauthorized-by-user-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead>Tentativi</TableHead>
                  <TableHead>Ultimo tentativo</TableHead>
                  <TableHead>Risorse</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.unauthorized_access_by_user_7d.map((u, i) => (
                  <TableRow key={`${u.user_id || 'unknown'}-${i}`} className={u.count >= 5 ? 'bg-red-50' : ''}>
                    <TableCell className="text-xs">
                      <div>{u.email || '—'}</div>
                      <div className="font-mono text-[10px] text-slate-400">{u.user_id}</div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        u.count >= 10 ? 'bg-red-200 text-red-900'
                          : u.count >= 5 ? 'bg-amber-200 text-amber-900'
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {u.count}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{fmtDate(u.last_attempt)}</TableCell>
                    <TableCell className="text-xs text-slate-600">{(u.resource_types || []).join(', ')}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{(u.ips || []).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <p className="text-xs text-slate-500 mt-2">
              Un utente con ≥5 tentativi verso risorse non sue riceve un <span className="text-red-700 font-medium">alert email</span> automatico all'admin.
              Valuta il blocco dell'account in caso di abuso.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ultimi audit log critici */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-purple-600" /> Ultimi audit log critici
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {data.recent_critical_logs.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Nessun log recente</p>
            ) : (
              <ul className="space-y-2" data-testid="critical-logs-list">
                {data.recent_critical_logs.map((e, i) => {
                  const Icon = EVENT_ICONS[e.action] || Activity;
                  return (
                    <li key={`${e.timestamp || ''}-${e.action || ''}-${i}`} className="flex items-start gap-2 text-xs border-b pb-2 last:border-0">
                      <Icon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800">{EVENT_LABELS[e.action] || e.action}</p>
                        <p className="text-slate-500 break-all">{e.user_email || '—'}</p>
                      </div>
                      <span className="text-slate-400 whitespace-nowrap">{fmtDate(e.timestamp)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Ultime richieste GDPR */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-emerald-600" /> Ultime richieste GDPR
              </span>
              <Button size="sm" variant="ghost" onClick={() => navigate('/admin/registro-gdpr')}>
                Vedi tutte →
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            {data.recent_gdpr_requests.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Nessuna richiesta registrata</p>
            ) : (
              <ul className="space-y-2" data-testid="gdpr-recent-list">
                {data.recent_gdpr_requests.map((r) => (
                  <li key={r.id} className="text-xs border-b pb-2 last:border-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800">{r.user_nome}</p>
                        <p className="text-slate-500 break-all">{r.user_email} · {r.tipo_richiesta}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${ESITO_BADGES[r.esito] || 'bg-slate-100 text-slate-700'}`}>
                        {r.esito}
                      </span>
                    </div>
                    <p className="text-slate-400 mt-1">{fmtDate(r.data_gestione)}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <p className="text-xs text-slate-400 text-center">
        Generato il {fmtDate(data.generated_at)} · Aggiorna per refresh dati
      </p>
        </TabsContent>

        <TabsContent value="audit" data-testid="audit-tab-content">
          {/* Embed AuditLogsPage: si auto-carica i log; padding gestito internamente */}
          <div className="-mx-6 -my-4">
            <AuditLogsPage />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityCenterPage;
